import { ff } from '@atlassian/jira-feature-flagging';
import { fetchJson, performPostRequest } from '@atlassian/jira-fetch';
import {
	isSynthetic,
	X2C_SERVICE_URL_PREFIX,
} from '@atlassian/jira-project-create-importer-common';
import type { X2CImportValidationError } from '../../../types';
import { transformToDateProperties, transformToUserEmailMappings } from '../../index';
import type {
	CSVData,
	CSVImportClient,
	PreviewApiTypes,
	StartApiTypes,
	UploadApiTypes,
	UploadUrlResponse,
	MetadataApiTypes,
	CSVMetadata,
	ValidateApiTypes,
} from '../types';
import {
	getSiteUrl,
	transformToValidateImportPayload,
	transformToValidateUserImportPayload,
} from '../utils';

export class NewProjectClient implements CSVImportClient {
	private BASE_URL: string;

	private serviceName?: string;

	constructor(cloudId: string, serviceName?: string) {
		this.BASE_URL = `${X2C_SERVICE_URL_PREFIX}/${cloudId}/import/csv`;
		this.serviceName = serviceName;
	}

	getUploadUrlAndMigrationId({ signal }: UploadApiTypes): Promise<UploadUrlResponse> {
		return fetchJson(`${this.BASE_URL}/uploadUrl`, {
			body: JSON.stringify({
				url: getSiteUrl(),
			}),
			method: 'POST',
			signal,
			headers: {
				'x-issynthetic-traffic': String(isSynthetic()),
			},
		});
	}

	getPreview({
		migrationId,
		projectData,
		advancedOptions,
		hasConfigFile,
	}: PreviewApiTypes): Promise<CSVData> {
		const payload = {
			fileEncoding: advancedOptions.fileEncoding.trim(),
			delimiter: advancedOptions.delimiter,
			dateProperties: transformToDateProperties(advancedOptions),
			projectDetails: {
				type: projectData.type,
				style: projectData.style,
				template: projectData.template,
			},
			hasConfigFile,
			...(this.serviceName && { serviceName: this.serviceName }),
		};
		return fetchJson(`${this.BASE_URL}/${migrationId}/preview`, {
			method: 'POST',
			body: JSON.stringify(payload),
			headers: {
				'x-issynthetic-traffic': String(isSynthetic()),
			},
		});
	}

	getMetadata({
		migrationId,
		mappingState,
		advancedOptions,
		hasConfigFile,
	}: MetadataApiTypes): Promise<CSVMetadata> {
		const payload = transformToValidateUserImportPayload(
			mappingState,
			advancedOptions,
			hasConfigFile,
		);

		return fetchJson(`${this.BASE_URL}/${migrationId}/metadata`, {
			body: JSON.stringify(payload),
			method: 'POST',
			headers: {
				'x-issynthetic-traffic': String(isSynthetic()),
			},
		});
	}

	startImport({
		migrationId,
		projectData,
		advancedOptions,
		mappingState,
	}: StartApiTypes): Promise<void> {
		const payload = {
			projectData,
			...transformToValidateImportPayload(
				mappingState,
				advancedOptions,
				ff('x2c-issuelinking-frontend_tuyzk'),
			),
			...(mappingState.shouldImportUsers && {
				userMapping: transformToUserEmailMappings(mappingState),
			}),
			...(this.serviceName && { serviceName: this.serviceName }),
		};
		return performPostRequest(`${this.BASE_URL}/${migrationId}/start`, {
			body: JSON.stringify(payload),
			headers: {
				'x-issynthetic-traffic': String(isSynthetic()),
				'Content-Type': 'application/json',
			},
		});
	}

	validateRequest({
		migrationId,
		mappingState,
		advancedOptions,
	}: ValidateApiTypes): Promise<X2CImportValidationError> {
		const payload = {
			...transformToValidateImportPayload(
				mappingState,
				advancedOptions,
				ff('x2c-issuelinking-frontend_tuyzk'),
			),
			...(this.serviceName && { serviceName: this.serviceName }),
		};
		return fetchJson(`${this.BASE_URL}/${migrationId}/validate`, {
			body: JSON.stringify(payload),
			method: 'POST',
			headers: {
				'x-issynthetic-traffic': String(isSynthetic()),
			},
		});
	}
}
